import type { CustomerDTO } from "@ifa/utils";

import { buildZuuFinancialGroups } from "./ZuuFinancialSection.helpers.js";
import styles from "./ZuuFinancialSection.module.css";

function toSnapshot(customer: CustomerDTO | null) {
  const financial = customer?.financial;
  if (!financial) return undefined;
  return {
    income: financial.income,
    expense: financial.expense,
    currentAssets: financial.currentAssets,
    nonCurrentAssets: financial.nonCurrentAssets,
    liabilities: financial.liabilities,
    protection: financial.protection,
  };
}

export function ZuuFinancialSection({ customer }: { customer: CustomerDTO | null }) {
  const snapshot = toSnapshot(customer);
  const groups = buildZuuFinancialGroups(snapshot);
  const hasFinancialData = Boolean(snapshot);

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div>
          <p className={styles.headerEyebrow}>Financial profile</p>
          <h3 className={styles.title}>財務資料</h3>
          <p className={styles.subtitle}>
            沿用現有 financial 欄位語義，將收入、支出、資產、負債與現有保障整理成長表單查看頁。
          </p>
        </div>
        <div className={hasFinancialData ? styles.statusBadge : styles.statusBadgePending}>
          {hasFinancialData ? "已接入資料" : "待補資料"}
        </div>
      </div>

      <div className={styles.notice}>
        {hasFinancialData
          ? "以下欄位按查看態重排，方便沿頁面順序核對客戶財務資料，不改後端欄位與原始資料含義。"
          : "客戶暫未提供完整 financial 資料，先保留查看頁結構與欄位位置，後續補錄後可直接沿同一版面核對。"}
      </div>

      <div className={styles.groupList}>
        {groups.map((group) => {
          const mutedSummary = group.summaryValue === "--";

          return (
            <article key={group.key} className={styles.groupCard}>
              <div className={styles.groupHeader}>
                <div>
                  <h4 className={styles.groupTitle}>{group.title}</h4>
                  <p className={styles.groupDescription}>{group.description}</p>
                </div>
                {group.summaryLabel ? (
                  <div className={styles.summaryBox}>
                    <span className={styles.summaryLabel}>{group.summaryLabel}</span>
                    <span className={mutedSummary ? styles.summaryValueMuted : styles.summaryValue}>
                      {group.summaryValue}
                    </span>
                  </div>
                ) : null}
              </div>

              <div className={styles.rows}>
                {group.rows.map((row) => {
                  const mutedValue = row.value === "--" || row.value === "無";

                  return (
                    <div key={`${group.key}-${row.code}`} className={styles.row}>
                      <div className={styles.rowCode}>{row.code}</div>
                      <div>
                        <div className={styles.rowLabel}>{row.label}</div>
                        {row.note ? <div className={styles.rowNote}>{row.note}</div> : null}
                      </div>
                      <div className={mutedValue ? styles.rowValueMuted : styles.rowValue}>{row.value}</div>
                    </div>
                  );
                })}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
